import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ViewerMode,
  ControlMode,
} from '../../models/model-viewer/viewer.models';

@Injectable({
  providedIn: 'root',
})
export class ViewerStateService {
  private viewerMode$ = new BehaviorSubject<ViewerMode>('NONE');
  public viewerMode: Observable<ViewerMode> = this.viewerMode$.asObservable();

  private isAddMarkerMode$ = new BehaviorSubject<boolean>(false);
  private isGizmoMode$ = new BehaviorSubject<boolean>(false);
  private isDrawing2DMode$ = new BehaviorSubject<boolean>(false);
  private isSelect2DMode$ = new BehaviorSubject<boolean>(false);

  public isAddMarkerMode: Observable<boolean> =
    this.isAddMarkerMode$.asObservable();
  public isGizmoMode: Observable<boolean> = this.isGizmoMode$.asObservable();
  public isDrawing2DMode: Observable<boolean> =
    this.isDrawing2DMode$.asObservable();
  public isSelect2DMode: Observable<boolean> =
    this.isSelect2DMode$.asObservable();

  setViewerMode(mode: ViewerMode): void {
    this.viewerMode$.next(mode);
    this.resetAllModes();
  }

  getViewerMode(): ViewerMode {
    return this.viewerMode$.value;
  }

  toggleAddMarkerMode(): void {
    const newState = !this.isAddMarkerMode$.value;
    this.isAddMarkerMode$.next(newState);

    if (newState) {
      this.isGizmoMode$.next(false);
    }
  }

  toggleGizmoMode(): void {
    const newState = !this.isGizmoMode$.value;
    this.isGizmoMode$.next(newState);

    if (newState) {
      this.isAddMarkerMode$.next(false);
    }
  }

  setAddMarkerMode(enabled: boolean): void {
    this.isAddMarkerMode$.next(enabled);
  }

  setGizmoMode(enabled: boolean): void {
    this.isGizmoMode$.next(enabled);
  }

  getAddMarkerMode(): boolean {
    return this.isAddMarkerMode$.value;
  }

  getGizmoMode(): boolean {
    return this.isGizmoMode$.value;
  }

  toggleDrawing2DMode(): void {
    const newState = !this.isDrawing2DMode$.value;
    this.isDrawing2DMode$.next(newState);

    if (newState) {
      this.isSelect2DMode$.next(false);
    }
  }

  toggleSelect2DMode(): void {
    const newState = !this.isSelect2DMode$.value;
    this.isSelect2DMode$.next(newState);

    if (newState) {
      this.isDrawing2DMode$.next(false);
    }
  }

  setDrawing2DMode(enabled: boolean): void {
    this.isDrawing2DMode$.next(enabled);
  }

  setSelect2DMode(enabled: boolean): void {
    this.isSelect2DMode$.next(enabled);
  }

  getDrawing2DMode(): boolean {
    return this.isDrawing2DMode$.value;
  }

  getSelect2DMode(): boolean {
    return this.isSelect2DMode$.value;
  }

  reset3DModes(): void {
    this.isAddMarkerMode$.next(false);
    this.isGizmoMode$.next(false);
  }

  reset2DModes(): void {
    this.isDrawing2DMode$.next(false);
    this.isSelect2DMode$.next(false);
  }

  resetAllModes(): void {
    this.reset3DModes();
    this.reset2DModes();
  }

  isAnyModeActive(): boolean {
    return (
      this.isAddMarkerMode$.value ||
      this.isGizmoMode$.value ||
      this.isDrawing2DMode$.value ||
      this.isSelect2DMode$.value
    );
  }

  getCurrentActiveMode(): ControlMode {
    if (this.isAddMarkerMode$.value) return 'marker';
    if (this.isGizmoMode$.value) return 'gizmo';
    if (this.isDrawing2DMode$.value) return 'draw';
    if (this.isSelect2DMode$.value) return 'select';
    return null;
  }
}
